<template>
    <div>
        <h1 class="display-heading">Medicine Delivery Detail</h1>

        <div class="card mb-3">
            <div class="card-header">Payment Details</div>
            <div class="card-body">
                <div v-if="!paymentSummary.isPaid && paymentDetails.companyCreditAutopayError && paymentDetails.companyCreditAutopayErrorMessage"
                    class="card mb-3">
                    <div class="card-body">
                        {{paymentDetails.companyCreditAutopayErrorMessage}}
                    </div>
                </div>
                <payment-detail ref="paymentDetail"
                    :paymentDetails="paymentDetails"
                    :paymentSummary="paymentSummary">
                    <button v-if="paymentReady" class="btn btn-primary btn-primary-insv" @click="showPaymentDisclaimer">Make Payment</button>
                </payment-detail>
            </div>
        </div>

        <popup-modal ref="popupModalConfirmation" :hasCloseButton="false" title="Disclaimer">
           <template slot="content">
               <p>Please understand and agree to all of our <a :href="termsAndConditionPatientsUrl" target="_blank">Terms & Conditions</a> before proceeding.</p>
           </template>
           <template slot="footer">
                <button type="button" class="btn btn-primary btn-primary-insv" @click="disclaimerProceed">Agreed & Proceed</button>
                <button type="button" class="btn btn-secondary" @click="disclaimerCancel">Cancel</button>
           </template>
        </popup-modal>

        <popup-modal ref="popupPaymentStatus" :hasCloseButton="true" :title="medicinePaymentStatusTitle">
           <template slot="content">
               <div v-html="medicinePaymentStatusText"></div>
           </template>
           <template slot="footer">
               <button v-if="this.medicinePaymentStatus===1" @click="moveToDashboardPage" class="btn btn-primary btn-primary-insv">Move to Dashboard Page</button>
               <button v-else @click="dismissPaymentStatus" class="btn btn-primary btn-primary-insv">Okay</button>
           </template>
        </popup-modal>
    </div>
</template>

<script>
import paymentDetail from '@/components/payment-detail';
import popupModal from '@/components/popup-modal';
export default {
    'name': 'MedicineDeliveryDetail',
    'components': {
        paymentDetail,
        popupModal
    },
    'computed': {
        consultationId () {
            return this.$route.params.id;
        },
        paymentReady () {
            return this.consultation.status === 1;
        },
        termsAndConditionPatientsUrl () {
            return this.$store.state.operator.termsAndConditionPatientsUrl;
        },
        medicinePaymentStatusTitle () {
            switch (this.medicinePaymentStatus) {
            case 0:
                return 'Payment Failed';
            case 1:
                return 'Payment Successful!';
            case 2:
                return 'Payment Pending.';
            }
        },
        medicinePaymentStatusText () {
            switch (this.medicinePaymentStatus) {
            case 0:
                return `
                    <p>
                        Sorry, your payment was unsuccessful.
                    </p>
                    <p>
                        Please retry again by making another payment.
                    </p>`;
            case 1:
                return `
                    <p>
                        Thank you, your payment has been successfully completed!
                    </p>
                    <p>
                        You are expected to receive your medicine(s) soon.
                    </p>
                    <p>
                        You can view medicine delivery status in "Dashboard" page. It changes to "Delivered" after doctor sending out your medication(s).
                    </p>`;
            case 2:
                return '<p>Thank you, your payment status is pending.</p>';
            }
        }
    },
    data () {
        return {
            'paymentDetails': {
                'paymentIdShort': null,
                'completedDate': null,
                'paymentRemarks': null,
                'companyCreditAutopayError': false,
                'companyCreditAutopayErrorMessage': null
            },
            'paymentSummary': {
                'isPaid': false,
                'paymentSummaryId': '',
                'paymentAmount': 0,
                'summaryItems': []
            },
            'consultation': {
                'status': 0
            },
            'paymentPrompt': {
                'paymentStatusTitle': '',
                'paymentStatusDetails': ''
            },
            'medicinePaymentStatus': 0
        };
    },
    mounted () {
        this.axios.get('Consultation/GetPatientMedicineDeliveryDetail', {
            'params': {
                'consultationId': this.consultationId
            }
        }).then(({ data }) => {
            let consultation = data.data;

            this.consultation.status = consultation.s;
            this.consultation.consultationIdShort = consultation.dcis;

            this.paymentDetails.paymentRemarks = `Payment for Consultation #${this.consultation.consultationIdShort}.`;
            if (consultation.ps) {
                this.initializePaymentDetails(consultation.ps);
                this.initializePaymentSummary(consultation.ps);
            }
        });

        if (this.$route.query && this.$route.query.status) {
            if (this.$route.query.status === '1') {
                this.medicinePaymentStatus = 1;
            } else if (this.$route.query.status === '6') {
                this.medicinePaymentStatus = 2;
            } else {
                this.medicinePaymentStatus = 0;
            }

            this.$refs.popupPaymentStatus.show();
        }
    },
    'methods': {
        initializePaymentDetails (paymentSummary) {
            this.paymentDetails.paymentIdShort = paymentSummary.psids;
            this.paymentDetails.completedDate = paymentSummary.cd;

            let companyCreditFailedRelatedCodes = [3, 4, 5];
            this.paymentDetails.companyCreditAutopayError = paymentSummary.ccaf && companyCreditFailedRelatedCodes.includes(paymentSummary.ccafc);

            if (this.paymentDetails.companyCreditAutopayError) {
                switch (paymentSummary.ccafc) {
                case 3:
                    this.paymentDetails.companyCreditAutopayErrorMessage = 'The required payment exceeding the set transaction limit given by your company, please pay either via FPX or credit card. Thereafter you can seek reimbursement from your employer later.';
                    break;
                case 4:
                    this.paymentDetails.companyCreditAutopayErrorMessage = 'The required payment exceeding the set annual limit given by your company, please pay either via FPX or credit card. Thereafter you can seek reimbursement from your employer later.';
                    break;
                case 5:
                    this.paymentDetails.companyCreditAutopayErrorMessage = "Due to your company's internal process, we are unable to approve the payment of this transaction. Please pay either via FPX or credit card. Thereafter you can seek reimbursement from your employer later.";
                    break;
                }
            }
        },
        initializePaymentSummary (paymentSummary) {
            this.paymentSummary.isPaid = paymentSummary.ip;
            this.paymentSummary.paymentSummaryId = paymentSummary.psid;
            this.paymentSummary.paymentAmount = paymentSummary.am;
            this.paymentSummary.summaryItems = paymentSummary.psi.map((item) => ({
                'itemName': item.in,
                'itemType': item.it,
                'itemDescription': item.id,
                'itemPrice': item.ip
            }));
        },
        showPaymentDisclaimer () {
            this.$refs.paymentDetail.validateOffsetWallet().then((isValid) => {
                if (isValid) {
                    this.$refs.popupModalConfirmation.show();
                }
            });
        },
        disclaimerProceed () {
            this.$refs.popupModalConfirmation.hide();
            this.$refs.paymentDetail.payNow();
        },
        disclaimerCancel () {
            this.$refs.popupModalConfirmation.hide();
        },
        dismissPaymentStatus () {
            this.$refs.popupPaymentStatus.hide();
        },
        moveToDashboardPage () {
            this.$refs.popupPaymentStatus.hide().then(() => {
                this.$router.push({
                    'name': 'dashboard-user'
                });
            });
        }
    }
};
</script>
